import {useCallback, useMemo} from "react";

import {
	anonymisedBlackFemaleCandidatesSelector,
	anonymisedBlackMaleCandidatesSelector,
	anonymisedEastAsianFemaleCandidatesSelector,
	anonymisedEastAsianMaleCandidatesSelector,
	anonymisedHispanicFemaleCandidatesSelector,
	anonymisedHispanicMaleCandidatesSelector,
	anonymisedMiddleEasternFemaleCandidatesSelector,
	anonymisedMiddleEasternMaleCandidatesSelector,
	anonymisedSouthAsianFemaleCandidatesSelector,
	anonymisedSouthAsianMaleCandidatesSelector,
	anonymisedWhiteFemaleCandidatesSelector,
	anonymisedWhiteMaleCandidatesSelector,
	anonymiseProfileModeTurnedOnSelector,
} from "store/candidate/candidateSelectors";

import useAppSelector from "hooks/useAppSelector";

import {SICandidate} from "api/candidateApi/types";

type AnonymisedItem = {
	full_name: string;
	photo: string;
	ethnicity: string;
	gender: string;
	// ...other anonymized fields as needed
};

type AnonymisedList = AnonymisedItem[];

/** Fisher - Yates shuffle (in-place) */
const shuffleInPlace = <T>(arr: T[]) => {
	for (let i = arr.length - 1; i > 0; i--) {
		const j = Math.floor(Math.random() * (i + 1));
		[arr[i], arr[j]] = [arr[j], arr[i]];
	}
};

/**
 * Hook: returns displayedCandidates; when anonymiseProfileModeTurnedOn is true,
 * picks anonymized items from the appropriate anonymized list.
 *
 * Behavior:
 *  - sortOrder = "asc": deterministic cycling through each list using a counter.
 *  - sortOrder = "random": consumes from a per-list shuffled queue without repeats;
 *    when the queue is empty, it reshuffles the source list and continues.
 */
const useAnonymiseCandidateData = (
	candidates: SICandidate[],
	sortOrder: "asc" | "random" = "random",
) => {
	const anonymisedWhiteMaleCandidates = useAppSelector(anonymisedWhiteMaleCandidatesSelector);
	const anonymisedWhiteFemaleCandidates = useAppSelector(anonymisedWhiteFemaleCandidatesSelector);

	const anonymisedHispanicMaleCandidates = useAppSelector(
		anonymisedHispanicMaleCandidatesSelector,
	);
	const anonymisedHispanicFemaleCandidates = useAppSelector(
		anonymisedHispanicFemaleCandidatesSelector,
	);

	const anonymisedSouthAsianMaleCandidates = useAppSelector(
		anonymisedSouthAsianMaleCandidatesSelector,
	);
	const anonymisedSouthAsianFemaleCandidates = useAppSelector(
		anonymisedSouthAsianFemaleCandidatesSelector,
	);

	const anonymisedEastAsianMaleCandidates = useAppSelector(
		anonymisedEastAsianMaleCandidatesSelector,
	);
	const anonymisedEastAsianFemaleCandidates = useAppSelector(
		anonymisedEastAsianFemaleCandidatesSelector,
	);

	const anonymisedMiddleEasternMaleCandidates = useAppSelector(
		anonymisedMiddleEasternMaleCandidatesSelector,
	);
	const anonymisedMiddleEasternFemaleCandidates = useAppSelector(
		anonymisedMiddleEasternFemaleCandidatesSelector,
	);

	const anonymisedBlackMaleCandidates = useAppSelector(anonymisedBlackMaleCandidatesSelector);
	const anonymisedBlackFemaleCandidates = useAppSelector(anonymisedBlackFemaleCandidatesSelector);

	const anonymiseProfileModeTurnedOn = useAppSelector(anonymiseProfileModeTurnedOnSelector);

	// Helper: map ethnicity+gender to a specific anonymized list.
	// An unknown gender counts as female and an unknown or "Other" ethnicity as white,
	// so every candidate gets a list and a real name is never shown.
	const getListFor = useCallback(
		(ethnicity?: string, gender?: string): AnonymisedList | undefined => {
			const e = (ethnicity || "").toLowerCase();
			const g = (gender || "").toLowerCase();
			const isMale = g.startsWith("m");

			if (e.includes("white")) {
				return isMale ? anonymisedWhiteMaleCandidates : anonymisedWhiteFemaleCandidates;
			}
			if (e.includes("hispanic") || e.includes("latino")) {
				return isMale
					? anonymisedHispanicMaleCandidates
					: anonymisedHispanicFemaleCandidates;
			}
			if (
				e.includes("south asian") ||
				e.includes("southasian") ||
				e.includes("indian") ||
				e.includes("pakistan") ||
				e.includes("bangladesh")
			) {
				return isMale
					? anonymisedSouthAsianMaleCandidates
					: anonymisedSouthAsianFemaleCandidates;
			}
			if (
				e.includes("east asian") ||
				e.includes("eastasian") ||
				e.includes("chinese") ||
				e.includes("korean") ||
				e.includes("japan") ||
				e.includes("japanese")
			) {
				return isMale
					? anonymisedEastAsianMaleCandidates
					: anonymisedEastAsianFemaleCandidates;
			}
			if (
				e.includes("middle eastern") ||
				e.includes("middle-eastern") ||
				e.includes("middleeastern") ||
				e.includes("arab")
			) {
				return isMale
					? anonymisedMiddleEasternMaleCandidates
					: anonymisedMiddleEasternFemaleCandidates;
			}
			if (e.includes("black") || e.includes("african")) {
				return isMale ? anonymisedBlackMaleCandidates : anonymisedBlackFemaleCandidates;
			}

			return isMale ? anonymisedWhiteMaleCandidates : anonymisedWhiteFemaleCandidates;
		},
		[
			anonymisedBlackFemaleCandidates,
			anonymisedBlackMaleCandidates,
			anonymisedEastAsianFemaleCandidates,
			anonymisedEastAsianMaleCandidates,
			anonymisedHispanicFemaleCandidates,
			anonymisedHispanicMaleCandidates,
			anonymisedMiddleEasternFemaleCandidates,
			anonymisedMiddleEasternMaleCandidates,
			anonymisedSouthAsianFemaleCandidates,
			anonymisedSouthAsianMaleCandidates,
			anonymisedWhiteFemaleCandidates,
			anonymisedWhiteMaleCandidates,
		],
	);

	const displayedCandidates = useMemo(() => {
		if (!anonymiseProfileModeTurnedOn) return candidates;

		// We will maintain two helper maps keyed by a stable id for each list.
		// Since arrays are objects and can be used as Map keys, but to be safer we create a stable key string.
		// Build a registry of lists with stable keys and references
		const registry: {key: string; list: AnonymisedList}[] = [];

		const addToRegistry = (list?: AnonymisedList) => {
			if (!list || list.length === 0) return;
			// Use the reference index in the registry as key to keep it stable per invocation
			const key = String(registry.length);
			registry.push({key, list});

			return key;
		};

		// Collect all possible lists we might use (avoid duplicates)
		const possibleLists = new Map<AnonymisedList, string>();
		[
			anonymisedWhiteMaleCandidates,
			anonymisedWhiteFemaleCandidates,
			anonymisedHispanicMaleCandidates,
			anonymisedHispanicFemaleCandidates,
			anonymisedSouthAsianMaleCandidates,
			anonymisedSouthAsianFemaleCandidates,
			anonymisedEastAsianMaleCandidates,
			anonymisedEastAsianFemaleCandidates,
			anonymisedMiddleEasternMaleCandidates,
			anonymisedMiddleEasternFemaleCandidates,
			anonymisedBlackMaleCandidates,
			anonymisedBlackFemaleCandidates,
		].forEach((lst) => {
			if (lst && lst.length > 0 && !possibleLists.has(lst)) {
				const key = addToRegistry(lst);
				if (key !== undefined) possibleLists.set(lst, key);
			}
		});

		// Maps keyed by registry key:
		// - counters for 'asc' mode
		const counters = new Map<string, number>();
		// - queues for 'random' mode (shuffled copies)
		const queues = new Map<string, AnonymisedList>();

		// Initialize queues for random mode: create shuffled copies
		if (sortOrder === "random") {
			registry.forEach(({key, list}) => {
				const copy = list.slice();
				shuffleInPlace(copy);
				queues.set(key, copy);
			});
		}

		// Function to get next anonymized item from a list depending on mode
		const nextFromList = (list?: AnonymisedList): AnonymisedItem | null => {
			if (!list || list.length === 0) return null;
			const key = possibleLists.get(list);
			// If, for some reason, the list wasn't registered (unlikely), register it now
			let registryKey = key as string;

			if (registryKey === undefined) {
				registryKey = addToRegistry(list) as string;

				possibleLists.set(list, registryKey);

				if (sortOrder === "random") {
					const copy = list.slice();
					shuffleInPlace(copy);
					queues.set(registryKey, copy);
				}
			}

			if (sortOrder === "asc") {
				const current = counters.get(registryKey) || 0;
				const item = list[current % list.length];
				counters.set(registryKey, current + 1);

				return item || null;
			}

			// random mode: use queue
			let q = queues.get(registryKey);
			if (!q || q.length === 0) {
				// refill and reshuffle
				q = list.slice();
				shuffleInPlace(q);
				queues.set(registryKey, q);
			}
			// pop from the end for O(1) removal
			const item = q.pop() || null;
			queues.set(registryKey, q);

			return item;
		};

		// Now map original candidates -> anonymized candidates
		return candidates.map((c) => {
			const list = getListFor(c.ethnicity, c.gender);
			// fallback to a general list if no specific list exists; optionally, you can use a global anonymized list here
			const chosen = nextFromList(list || undefined);

			if (!chosen) {
				// if there's no matching-anonymized item, return a candidate unchanged
				return c;
			}

			// Merge: anonymized fields take precedence.
			// Keep original candidate id and other non-identifiable fields if needed.
			// We replace name and avatar_url (photo), and spread other anonymized fields on top.
			return {
				...c,
				full_name: chosen.full_name || c.full_name,
				avatar_url: chosen.photo || c.avatar_url,
				// apply any other anonymized fields (careful not to overwrite essential system ids)
				...(chosen as object),
			} as SICandidate;
		});
		// Dependencies: include all lists and inputs so memo updates when they change
	}, [
		anonymiseProfileModeTurnedOn,
		candidates,
		anonymisedWhiteMaleCandidates,
		anonymisedWhiteFemaleCandidates,
		anonymisedHispanicMaleCandidates,
		anonymisedHispanicFemaleCandidates,
		anonymisedSouthAsianMaleCandidates,
		anonymisedSouthAsianFemaleCandidates,
		anonymisedEastAsianMaleCandidates,
		anonymisedEastAsianFemaleCandidates,
		anonymisedMiddleEasternMaleCandidates,
		anonymisedMiddleEasternFemaleCandidates,
		anonymisedBlackMaleCandidates,
		anonymisedBlackFemaleCandidates,
		sortOrder,
		getListFor,
	]);

	return {
		displayedCandidates,
	};
};

export default useAnonymiseCandidateData;
